import type { EverywhereTest } from 'components/Everywhere/data';
import { findTestByProductSlug, testPath } from 'components/Everywhere/helpers';
import { useEffect, useMemo, useRef } from 'react';
import { trackEva } from 'utils/eva';
import type { ProductRecommendation } from 'utils/eva';
import { useProductSwapExperiments } from 'utils/hooks';

import { assignEverywhereLabVisitImages } from '../components/products/EverywhereSearchProductCard';
import type { ProductData } from '../components/products/products.types';
import { useAvailableEverywhereProductSlugs } from './useAvailableEverywhereProductSlugs';
import { useProducts } from './useProducts';

/** A recommendation that resolved to something renderable. */
export type ChatRecommendationItem =
  | { kind: 'product'; slug: string; product: ProductData }
  | {
      kind: 'everywhere';
      slug: string;
      test: EverywhereTest;
      imageIndex: number;
    };

const is360Slug = (slug: string): boolean =>
  slug.includes('360-full-body-test');

export function useChatProductRecommendations(
  recommendations: ProductRecommendation,
) {
  const productsBySlug = useProducts();
  const availableEverywhereSlugs = useAvailableEverywhereProductSlugs();
  const lastTrackedKeyRef = useRef<string | null>(null);
  // Presence is scoped to the full recommendation set, not just the rendered
  // top 3. A governed product sitting beyond index 3 in control therefore fires
  // exposure without being seen — accepted as a minor control-side artifact,
  // since in variant it can be pulled into the top 3, and scoping it tighter is
  // circular with the reorder (we'd need the resolved variant to know what renders).
  const { applyToProductList, applyToDisplayName } = useProductSwapExperiments(
    recommendations.products ?? [],
  );

  const { productSlugs, visibleItems, hasEverlywell360 } = useMemo(() => {
    const slugs = applyToProductList(
      recommendations.products ?? [],
      (slug) => slug,
    ).slice(0, 3);

    // Everywhere tests the store does not sell in this environment have no
    // price or test page, so they are dropped the same way an out-of-stock
    // at-home product is.
    const sellableEverywhere = new Set(availableEverywhereSlugs);

    const items = slugs.flatMap<ChatRecommendationItem>((slug) => {
      // The 360 has a dedicated card rendered outside the grid.
      if (is360Slug(slug)) return [];

      if (sellableEverywhere.has(slug)) {
        const test = findTestByProductSlug(slug);
        return test ? [{ kind: 'everywhere', slug, test, imageIndex: 0 }] : [];
      }

      const product = productsBySlug[slug];
      // Exclude products that are out of stock so customers never see an
      // unbuyable recommendation.
      if (!product || product.outOfStock) return [];

      return [
        {
          kind: 'product',
          slug,
          // Present products under their experiment display name (e.g. the
          // consolidated Food Sensitivity variant renames the base product).
          product: {
            ...product,
            displayName: applyToDisplayName(slug, product.displayName),
          },
        },
      ];
    });

    // Photos are assigned across the whole set at once so no two Everywhere
    // cards in the same response share an image.
    const imageIndexes = assignEverywhereLabVisitImages(
      items.map((item) => (item.kind === 'everywhere' ? item.test.name : null)),
      0,
    );

    return {
      productSlugs: slugs,
      visibleItems: items.map((item, idx) =>
        item.kind === 'everywhere'
          ? { ...item, imageIndex: imageIndexes[idx] ?? 0 }
          : item,
      ),
      hasEverlywell360: slugs.some(is360Slug),
    };
  }, [
    productsBySlug,
    recommendations.products,
    applyToProductList,
    applyToDisplayName,
    availableEverywhereSlugs,
  ]);

  useEffect(() => {
    if (productSlugs.length === 0) return;

    // Track once per unique product/follow-up set to avoid duplicate events.
    const followUps = recommendations.followUpQuestions ?? null;
    const key = JSON.stringify({ productSlugs, followUps });
    if (lastTrackedKeyRef.current === key) return;
    lastTrackedKeyRef.current = key;

    const products = productSlugs.map((slug) => {
      const test = findTestByProductSlug(slug);
      if (test) {
        return {
          slug,
          name: test.name,
          url: testPath(test.name),
          product_type: 'everywhere',
        };
      }

      const product = productsBySlug[slug];
      const url = `/products/${slug}/`;
      if (!product) {
        return { slug, url };
      }
      return {
        id: product.productId,
        variant_id: product.variantId,
        slug,
        name: product.displayName,
        price: product.price,
        url,
      };
    });

    trackEva('Eva Search Recommended Products', {
      products,
      experience: 'conversation',
    });
  }, [productSlugs, productsBySlug, recommendations.followUpQuestions]);

  return {
    productSlugs,
    visibleItems,
    hasEverlywell360,
  };
}
